import {useEffect, useMemo, useState} from 'react'
import {PartialEntry} from '@github-ui/react-core/partial-entry'
import {VALUES, CreateIssueDialogEntry} from '..'
import {noop} from '@github-ui/noop'
import {RelayEnvironmentProvider} from 'react-relay'
import {relayEnvironmentWithMissingFieldHandlerForNode} from '@github-ui/relay-environment'
import {type Root, createRoot} from 'react-dom/client'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {getSafeConfig} from '../utils/option-config'

export class IssueCreateDialogElement extends HTMLElement {
  root: Root | undefined
  connectedCallback() {
    const container = document.createElement('div')
    this.root = createRoot(container)
    this.append(container)
    this.render()
  }

  static get observedAttributes() {
    return ['isenabled', 'key', 'owner', 'repository']
  }

  attributeChangedCallback() {
    if (!this.isConnected) return
    this.render()
  }

  render() {
    const isEnabled = this.getAttribute('isenabled') === 'true'
    const key = this.getAttribute('key')
    const owner = this.getAttribute('owner')
    const repository = this.getAttribute('repository')
    if (!this.root || !isEnabled || !key || key.length < 1) return

    const el = (
      <PartialEntry
        partialName="issue-create"
        embeddedData={{props: {isEnabled, key, owner, repository}}}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Component={WrapperComponent as any}
        wasServerRendered={false}
        ssrError={Boolean(false)}
      />
    )

    this.root.render(el)
  }
}

const environment = relayEnvironmentWithMissingFieldHandlerForNode()

function WrapperComponent(props: CreateIssueModalProps) {
  return (
    <RelayEnvironmentProvider environment={environment}>
      <CreateIssueModal {...props} />
    </RelayEnvironmentProvider>
  )
}

type CreateIssueModalProps = {
  key: string
  owner?: string
  repository?: string
}

function CreateIssueModal({key, owner, repository}: CreateIssueModalProps) {
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    setIsVisible(true)
  }, [key])

  const repo = useMemo(
    () =>
      owner && repository
        ? {
            repository: {
              owner,
              name: repository,
            },
          }
        : undefined,
    [owner, repository],
  )

  if (!isVisible) return null

  // TODO: setup real value for the config below
  return (
    <CreateIssueDialogEntry
      navigate={noop}
      isCreateDialogOpen={isVisible}
      setIsCreateDialogOpen={setIsVisible}
      optionConfig={getSafeConfig({
        storageKeyPrefix: VALUES.storageKeyPrefixes.globalAdd,
        pasteUrlsAsPlainText: false,
        singleKeyShortcutsEnabled: true,
        useMonospaceFont: false,
        issueCreateArguments: repo,
        showFullScreenButton: false,
      })}
    />
  )
}

if (ssrSafeWindow && !ssrSafeWindow.customElements.get('issue-create-dialog')) {
  ssrSafeWindow.customElements.define('issue-create-dialog', IssueCreateDialogElement)
}

try{ WrapperComponent.displayName ||= 'WrapperComponent' } catch {}
try{ CreateIssueModal.displayName ||= 'CreateIssueModal' } catch {}